import { useState, type ChangeEvent } from 'react';
import { Button, ButtonToggle, InputPlain } from '@peacecover/component';
import { ArrowLeft } from '@peacecover/icon';
import { useVirtualKeyboardVisible } from '../../chat/hooks/useVirtualKeyboardVisible';
import type { OccupationType } from '../types';

const JOB_OPTIONS: { label: string; occupationType: OccupationType }[] = [
  { label: '가수 / 아이돌', occupationType: 'SINGER' },
  { label: '배우', occupationType: 'ACTOR' },
  { label: '방송인', occupationType: 'ENTERTAINER' },
  { label: '웹툰/웹소설 캐릭터', occupationType: 'CHARACTER' },
  { label: '기타(ex. 모델, 스트리머 등...)', occupationType: 'ETC' }
];

export type EnterArtistInfoSubmitPayload = {
  occupationType?: OccupationType;
  occupationDescription?: string;
};

interface EnterArtistInfoStepProps {
  nickname: string;
  onBack: () => void;
  onSubmit: (partial: EnterArtistInfoSubmitPayload) => void;
}

const EnterArtistInfoStep = ({ nickname, onBack, onSubmit }: EnterArtistInfoStepProps) => {
  const [selectedJob, setSelectedJob] = useState<string>('');
  const [groupName, setGroupName] = useState('');
  const { isKeyboardVisible, handleFocus, handleBlur } = useVirtualKeyboardVisible();

  const handleJobSelect = (job: string) => {
    setSelectedJob((prev) => (prev === job ? '' : job));
  };

  return (
    <div className="pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] min-h-screen flex flex-col bg-background-dark">
      <header className="px-[16px] py-[15px]">
        <button type="button" onClick={onBack}>
          <ArrowLeft className="text-pure-white" />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto pb-[120px]">
        <div className="flex flex-col items-start gap-[14px] px-[24px] py-[10px]">
          <h1 className="text-[26px] font-bold text-pure-white leading-[31px]">
            입력한 {nickname} 이름으로는
            <br />
            충분한 정보를 찾기 어려웠어요
          </h1>
          <p className="text-[15px] font-normal text-main-light-4 leading-[18px]">
            인물에 대한 정보를 직접 입력해주세요
          </p>
        </div>

        {/* Job Section */}
        <div className="mt-[20px] px-[24px]">
          <p className="text-[17px] font-semibold text-pure-white leading-[20px] mb-[12px]">
            활동 분야는 무엇인가요?
          </p>
          <div className="flex flex-col">
            {JOB_OPTIONS.map(({ label }) => (
              <div key={label} className="flex flex-col gap-[10px] py-[18px]">
                <button
                  type="button"
                  className="flex items-center gap-[10px] text-left"
                  onClick={() => handleJobSelect(label)}
                >
                  <ButtonToggle
                    size="lg"
                    type="check"
                    checked={selectedJob === label}
                    className="text-sub-dark-2 [--color-text-sub-lv3:currentColor] [--color-surface-lv2:var(--color-white-opacity-50)]"
                  />
                  <span className="text-[15px] font-semibold text-main-dark-2 leading-[18px]">
                    {label}
                  </span>
                </button>

                {label === '가수 / 아이돌' && selectedJob === '가수 / 아이돌' ? (
                  <div className="pl-[34px]">
                    <div className="px-[16px] py-[17px] bg-secondary-dark rounded-[12px]">
                      <InputPlain
                        placeholder="그룹명 또는 솔로"
                        className="w-full bg-transparent text-[17px] font-semibold !text-main-dark-2 placeholder:text-placeholder-dark"
                        value={groupName}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          setGroupName(e.target.value)
                        }
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>

        {/* Random Info Button */}
        <div className="mt-[40px] flex justify-center flex-col items-center">
          <p className="text-[13px] font-semibold text-main-light-4">
            자세히 입력할 수록 이야기의 흐름이 탄탄해져요
          </p>
        </div>
      </div>

      {/* Footer Button */}
      <div
        className={`fixed bottom-0 left-0 right-0 px-[24px] w-full pt-[10px] pb-[calc(10px+env(safe-area-inset-bottom))] bg-background-dark transition-all duration-150 ease-in-out ${
          isKeyboardVisible
            ? 'translate-y-full opacity-0 pointer-events-none'
            : 'translate-y-0 opacity-100'
        }`}
        aria-hidden={isKeyboardVisible}
      >
        <Button
          size="large"
          className="w-full text-[17px] font-semibold"
          onClick={() => {
            const option = JOB_OPTIONS.find((o) => o.label === selectedJob);
            const payload: EnterArtistInfoSubmitPayload = {};
            if (option) {
              payload.occupationType = option.occupationType;
              const desc = groupName.trim();
              if (desc) payload.occupationDescription = desc;
            }
            onSubmit(payload);
          }}
        >
          이걸로 만들어 볼래요
        </Button>
      </div>
    </div>
  );
};

export default EnterArtistInfoStep;
